<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import TabIcon from './TabIcon.vue'
import { PAGES } from './constants'
import { navigationStore, useNavigation } from './useNavigation'
import { useStudentScreen } from './useStudentScreen'
import { Holiday, useHoliday } from '~/composables/useHoliday'
import { pikimoni } from '@/helpers/pikimoni'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'
import InlineSvg from 'vue-inline-svg'

const asset = useStaticAsset()

const props = defineProps<{ context: Student2Context }>()
const emit = defineEmits(['update:tab', 'update:hidden'])

const { navigationState, getLink, gotoOnClick, goto, currentUnitId } = useNavigation()
const { isMobileLocal } = useStudentScreen()
const { notify } = useNotification()

const tabs = computed(() => PAGES.filter(entry => entry.tab))
const isFloating = computed(() => PAGES.find(entry => entry.name === navigationState.params.tab)?.floatingNav ?? false)
const audioWarning = computed(() => props.context.smartbook?.lock_reason === 'out_of_charge')
const smartbookDemo = computed(() => props.context.smartbook?.demo)

const smartbookLocked = computed(() => {
  if (smartbookDemo.value) return false
  if (!props.context.smartbook) return true
  if (!props.context.smartbook.unlocked) return true
  return false
})

const unitLockReason = computed(() => {
  return props.context.course_part.units.find(entry => entry.id === currentUnitId.value)?.lock_reason
})

function toggleHidden() {
  navigationStore.toggleHidden()
}

async function loginToPikimoni() {
  const popup = window.open('about:blank', '_blank')
  if (!popup) return

  try {
    const url = await pikimoni.getLoginUrl('student', props.context.student.id)
    popup.location.href = url
  } catch (error: unknown) {
    console.error(error)
    popup.close()

    let title = 'Не удалось перейти ко входу в Pikimoni'
    if (typeof error === 'string') title = error
    else if (error instanceof Error) title = error.message
    notify({ type: 'error', title })
  }
}

function isUnderConstruction(tab: { name: string; smartbook?: boolean }) {
  if (!tab.smartbook) return false

  const developmentState = props.context.smartbook?.development_state || 'full'
  switch (developmentState) {
    case 'full':
      return false
    case 'video_only':
      return tab.name !== 'video'
    case 'not_ready':
      return true
    default:
      return false
  }
}

watch(
  () => navigationState.params,
  obj => {
    for (const [key, value] of Object.entries(obj)) {
      emit(`update:${key}` as 'update:tab' | 'update:hidden', value)
    }

    if (obj.tab !== 'home' && unitLockReason.value === 'need_login') {
      goto(getLink({ tab: 'home' }))
    }
  },
  { deep: true }
)

watch(
  () => props.context,
  () => {
    navigationStore.setSmartbookAvailable(!!props.context.smartbook)
  },
  { immediate: true }
)

const onPopState = () => navigationStore.readLocation()
const onHashChange = () => navigationStore.readLocation()

navigationStore.readLocation()
if (unitLockReason.value === 'need_login') {
  goto(getLink({ tab: 'home' }))
}

onMounted(() => {
  window.addEventListener('popstate', onPopState)
  window.addEventListener('hashchange', onHashChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState)
  window.removeEventListener('hashchange', onHashChange)
})

const holiday = useHoliday()

const logoAsset = computed(() => {
  switch (holiday) {
    case Holiday.NewYear:
      return asset('img/logo-small-newyear.svg')
    case Holiday.Valentine:
      return asset('img/logo-small-valentine.svg')
    case Holiday.WomensDay:
      return asset('img/logo-small-womensday.svg')
    case Holiday.Easter:
      return asset('img/logo-small-easter.svg')
    case Holiday.ChildsDay:
      return asset('img/logo-small-childsday.svg')
    case Holiday.KnowledgeDay:
      return asset('img/logo-small-knowledgeday.svg')
    case Holiday.TeachersDay:
      return asset('img/logo-small-teachersday.svg')
    default:
      return asset('img/logo-small.svg')
  }
})
</script>

<template>
  <nav
    class="student-navigation"
    :class="{ 'student-navigation_hidden': navigationState.params.hidden, 'student-navigation_floating': isFloating }"
  >
    <button class="student-navigation__toggle" @click="toggleHidden">
      <v-icon v-if="navigationState.params.hidden" key="1">logo-small</v-icon>
      <v-icon v-else key="0">mdi-chevron-left</v-icon>
    </button>

    <div class="student-navigation__content">
      <v-link
        :href="getLink({ tab: 'home' })"
        data-nav-item="home"
        class="student-navigation__logo"
        @click="gotoOnClick"
        v-tooltip:right="'Главная'"
      >
        <InlineSvg
          :width="isMobileLocal ? 48 : 64"
          :height="isMobileLocal ? 48 : 64"
          :src="logoAsset"
          class="student-navigation__logo"
        />
      </v-link>

      <ul class="student-navigation__tabs" :class="{ 'student-navigation__tabs_disabled': unitLockReason }">
        <li v-for="tab in tabs" :key="tab.name" v-tooltip:right="tab.text">
          <v-link
            :href="getLink({ tab: tab.name })"
            :disabled="tab.smartbook === true && (smartbookLocked || isUnderConstruction(tab))"
            :data-nav-item="tab.name"
            @click="gotoOnClick"
          >
            <transition-group name="fade" tag="div" class="student-navigation__transition">
              <div v-if="navigationState.params.tab === tab.name" key="1" class="student-navigation__icon-container">
                <TabIcon
                  :tab="tab.name"
                  :size="isMobileLocal ? 48 : 64"
                  active
                  :warning="tab.name === 'audio' && audioWarning"
                  :demo="tab.smartbook === true && smartbookDemo"
                />
              </div>
              <div v-else key="0" class="student-navigation__icon-container">
                <TabIcon
                  :class="isMobileLocal ? 'ma-half' : 'ma-1'"
                  :tab="tab.name"
                  :size="isMobileLocal ? 40 : 48"
                  :warning="tab.name === 'audio' && audioWarning"
                  :demo="tab.smartbook === true && smartbookDemo"
                  :disabled="tab.smartbook === true && (smartbookLocked || isUnderConstruction(tab))"
                />
              </div>
            </transition-group>
          </v-link>
        </li>

        <li>
          <a class="d-flex flex-column align-center" href="https://nebdeti.ru/" target="_blank">
            <v-icon class="blue-text" size="36" style="opacity: 0.8">mdi-book-open-variant</v-icon>
            <span class="text-caption text-center">БИБЛИО&shy;ТЕКА</span>
          </a>
        </li>
      </ul>

      <v-link v-if="context.student.has_pikimoni_binding" @click="loginToPikimoni">
        <v-icon size="64" class="white-text">student-v2/pikimoni</v-icon>
      </v-link>
    </div>
  </nav>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

:root {
  --navbar_width: 112px;
}

@include s2.mobile {
  :root {
    --navbar_width: 72px;
  }
}

.student-navigation {
  width: var(--navbar_width);
  height: 100vh;
  height: 100svh;

  background: white;
  box-shadow:
    4px 0px 20px 0px #00000020,
    2px 0px 4px 0px #0000000a;

  transform: translateX(0);
  transition:
    transform 0.3s ease,
    width 0.3s ease;

  position: relative;
  z-index: 2;

  &_hidden {
    // own width + 20px = shadow
    transform: translateX(calc((-1 * var(--navbar_width)) - 20px));
  }

  &_hidden &__toggle {
    // 100% = to be outside of the navbar
    // 20px = offset from the navbar to the start of the viewport
    // 24px = base button's margin
    left: calc(100% + 20px + 24px);

    @include s2.mobile {
      left: calc(100% + 16px + 16px);
    }
  }

  &_floating {
    position: absolute;
  }

  &__logo {
    overflow: visible;
  }

  &__newyear-hat {
    position: absolute;
    top: -16px;
    left: 16px;

    @include s2.mobile {
      top: -10px;
      left: 10px;
    }
  }

  &__content {
    padding: 32px 15px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    height: 100%;
    overflow-y: auto;

    @include s2.mobile {
      padding: 12px;
      gap: 4px;
    }
  }

  &__toggle {
    position: absolute;
    z-index: 0;
    top: 24px;
    left: calc(100% + 24px);
    transition: left 0.3s ease;

    cursor: pointer;
    box-shadow:
      0px 4px 20px 0px #00000026,
      0px 2px 4px 0px #00000033;
    border: none;
    background: white;
    padding: 8px;
    border-radius: 8px;

    @include s2.mobile {
      top: 8px;
      left: calc(100% + 8px);
    }
  }

  a.v-link:focus {
    opacity: 1;
  }

  &__tabs {
    flex: 1;

    display: flex;
    flex-direction: column;
    gap: 12px;

    @include s2.mobile {
      gap: 4px;
    }

    li {
      width: 64px;
      height: 64px;
      flex: 0 0 64px;
      position: relative;

      & > a {
        position: absolute;
      }

      @include s2.mobile {
        width: 48px;
        height: 48px;
        flex: 0 0 48px;
      }
    }

    &_disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__transition {
    position: relative;
  }

  &__icon-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 64px;
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;

    @include s2.mobile {
      width: 48px;
      height: 48px;
    }
  }

  &__avatar {
    background: $lightenGray;
    border: 1px solid $lightGray;
    border-radius: 12px;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 64px;
    height: 64px;
    flex: 0 0 64px;

    @include text-title;
  }
}

@keyframes navigation-appear {
  0% {
    transform: translateX(-150%);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
